import Calendar from "react-calendar/dist/entry.nostyle"
import { Component } from "react"

import { Spinner } from "../../../components/Spinner"
import { Analytics } from "../../../utils/analytics"
import * as utils from "../../../utils/utils"
import { httpClient } from "../../../utils/infrastructure/httpClient"

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000

/*
type Props = {
    psy: Object,
    serviceType: ServiceType,
    onHourSelected: Date => void,
    client: Object,
}
*/

export class BookingCalendar extends Component {
    static twoWeeks = 14 * 24 * 3600000
    state = {
        fetching: true,
        firstDayOfMonth: this.firstDayOfMonthForDate(new Date()),
        selectedDayOfMonth: new Date(),
        allowedDates: [],
        availableLocalHours: {},
        slotIdsByDate: {},
    }

    componentDidMount() {
        this.fetchAvailableHours(this.preprocessAvailableHours)
    }

    tryRunAnalyticsEvents(allowedDates, earliestSessionDatetime, slotIdsByDate) {
        if (this.skipFreeSlotAnalytics) return
        this.skipFreeSlotAnalytics = true
        const currentDatetime = new Date()
        if (this.props.source && this.props.source.mode === "competence_questionnaire") {
            if (allowedDates.length === 0) {
                Analytics.no_free_slots({ psy: this.props.psy })
            } else {
                if (earliestSessionDatetime - currentDatetime > BookingCalendar.twoWeeks) {
                    Analytics.free_slot_too_far_away({ psy: this.props.psy })
                }
            }
        }
        const currentDateMs = currentDatetime.getTime()
        Object.keys(slotIdsByDate).forEach((date) => {
            const ms = new Date(date).getTime()
            if (ms - currentDateMs <= BookingCalendar.twoWeeks) {
                Analytics.free_slot()
            }
        })
    }

    firstDayOfMonthForDate(dt) {
        return new Date(dt.getFullYear(), dt.getMonth(), 1, 12, 0, 0)
    }

    fetchAvailableHours(next) {
        const range = this.getAvailableHoursFetchingDateRange(this.state.firstDayOfMonth)
        const data = {
            psy_hash_id: this.props.psy.hash_id,
            from_date: range.from,
            to_date: range.to,
            offset_from_utc_minutes: -new Date().getTimezoneOffset(),
            service_type: this.props.serviceType,
            client_data: this.props.client,
            for_psychologist: this.props.for_psychologist,
        }
        httpClient
            .post("psychologist/events/available-hours", data)
            .then((data) => {
                next(data)
            })
            .catch((e) => {
                next(e)
            })
    }

    getAvailableHoursFetchingDateRange(targetDate) {
        const targetDateTimestamp = targetDate.getTime()
        const fromDays = 1
        const toDays = 80

        const from = new Date(targetDateTimestamp - fromDays * DAY_IN_MILLISECONDS)
        const to = new Date(targetDateTimestamp + toDays * DAY_IN_MILLISECONDS)
        return {
            from: utils.formatLocalDateIso(from),
            to: utils.formatLocalDateIso(to),
        }
    }

    preprocessAvailableHours = (response) => {
        if (response.error) {
            this.setState({
                fetching: false,
                errorMessage: response.error,
            })
            return
        }

        /*
        {
            [dateStr]:  [
                { time: "..." id: "..."},   // slot
                ...
            ]
        }
        */
        const availableSlots = response
        const availableLocalHours = {}
        const localDatesHavingSlots = new Set()
        const minHour = 23
        const maxHour = 0
        let earliestSessionDatetime = false

        const slotIdsByDate = {}

        for (const utcDateStr in availableSlots) {
            const dateMatch = utcDateStr.match(/^(\d+)-(\d+)-(\d+)$/)
            if (!dateMatch) {
                continue
            }
            const sessionStartDatetime = new Date(
                Date.UTC(dateMatch[1], parseInt(dateMatch[2], 10) - 1, dateMatch[3])
            )
            if (!earliestSessionDatetime) {
                earliestSessionDatetime = new Date(sessionStartDatetime)
            }

            for (const slot of availableSlots[utcDateStr]) {
                const utcTimeStr = slot.time

                const timeMatch = utcTimeStr.match(/^(\d+):(\d+)(?: (\d+))?$/)
                sessionStartDatetime.setUTCHours(timeMatch[1], timeMatch[2])

                const localSessionDayStart = new Date(sessionStartDatetime).setHours(0)
                localDatesHavingSlots.add(localSessionDayStart)

                const durationIsPresent = timeMatch[3] !== undefined
                const sessionEndDatetime = new Date(
                    sessionStartDatetime.getTime() +
                        (durationIsPresent ? timeMatch[3] : 60) * 60 * 1000
                )

                const key = utils.formatLocalDate(sessionStartDatetime)
                if (!availableLocalHours.hasOwnProperty(key)) {
                    availableLocalHours[key] = []
                }

                let sessionTimeStr = utils.formatLocalTime(sessionStartDatetime)
                if (durationIsPresent) {
                    sessionTimeStr += " - " + utils.formatLocalTime(sessionEndDatetime)
                }
                availableLocalHours[key].push(sessionTimeStr)

                slotIdsByDate[sessionStartDatetime.toUTCString()] = slot.id
            }
        }

        const allowedDates = Array.from(localDatesHavingSlots)
            .sort()
            .map((secondsSinceEpoch) => new Date(secondsSinceEpoch))

        this.tryRunAnalyticsEvents(allowedDates, earliestSessionDatetime, slotIdsByDate)

        const currentMonth = this.state.firstDayOfMonth.getMonth()
        const currentYear = this.state.firstDayOfMonth.getYear()

        let firstAvailableDateInCurrentMonth = this.state.selectedDayOfMonth

        for (const date of allowedDates) {
            if (
                date.getYear() < currentYear ||
                (date.getYear() === currentYear && date.getMonth() < currentMonth)
            ) {
                continue
            }

            firstAvailableDateInCurrentMonth = date
            break
        }

        this.setState({
            fetching: false,
            errorMessage: undefined,
            availableLocalHours: availableLocalHours,
            minHour: minHour,
            maxHour: maxHour,
            selectedDayOfMonth: firstAvailableDateInCurrentMonth,
            allowedDates,
            slotIdsByDate,
        })
    }

    onDateSelected = (date) => {
        this.setState({ selectedDayOfMonth: date })
    }

    onCalendarViewChanged = ({ activeStartDate, view }) => {
        if (view === "month") {
            this.setState(
                {
                    firstDayOfMonth: activeStartDate,
                    selectedDayOfMonth: activeStartDate,
                    fetching: true,
                },
                () => this.fetchAvailableHours(this.preprocessAvailableHours)
            )
        }
    }

    renderSelectedDayAvailableHours() {
        const hours =
            this.state.availableLocalHours[utils.formatLocalDate(this.state.selectedDayOfMonth)]
        if (!hours)
            return (
                <div className="alert alert-danger text-center" role="alert">
                    В этот день нет времени, доступного для записи. Выберите другую дату.
                </div>
            )
        return (
            <div className="available-hours-container">
                {hours.map((hour, i) => {
                    const match = hour.match(/^(\d+):(\d+)/)
                    const sessionDatetime = new Date(this.state.selectedDayOfMonth)
                    sessionDatetime.setHours(parseInt(match[1], 10), parseInt(match[2], 10), 0)
                    const cellId = this.makeHourKey(sessionDatetime)

                    const slotId = this.state.slotIdsByDate[sessionDatetime.toUTCString()]

                    return (
                        <div
                            key={`${cellId}${i}`}
                            className="available-hour"
                            onClick={() => {
                                Analytics.session_time_selected()
                                this.props.onHourSelected(sessionDatetime, slotId)
                            }}
                        >
                            {hour}
                        </div>
                    )
                })}
            </div>
        )
    }

    makeHourKey(date, hours = undefined) {
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}-${
            hours !== undefined ? hours : date.getHours()
        }-${date.getMinutes()}`
    }

    calendarTileDisabled = ({ date: currentDate, view }) => {
        const minDate = new Date(new Date().setHours(0, 0, 0, 0))
        const maxDate =
            this.state.allowedDates.length > 0
                ? new Date(
                      this.state.allowedDates[this.state.allowedDates.length - 1].getTime() +
                          DAY_IN_MILLISECONDS
                  )
                : new Date()

        switch (view) {
            case "month":
                return (
                    currentDate < minDate ||
                    currentDate > maxDate ||
                    !this.state.availableLocalHours.hasOwnProperty(
                        utils.formatLocalDate(currentDate)
                    )
                )
            case "year":
                return currentDate.getMonth() < minDate.getMonth()
            default:
                return false
        }
    }

    render() {
        return this.state.fetching ? (
            <div className="booking-widget__fetching-spinner">
                <Spinner />
            </div>
        ) : (
            <>
                {this.state.errorMessage ? (
                    <div className="alert alert-danger text-center" role="alert">
                        {this.state.errorMessage}
                    </div>
                ) : (
                    <p className="lead font-futura-500">
                        Кликните на нужную дату, чтобы увидеть часы приема, доступные в этот день.
                    </p>
                )}

                <div className="client-timezone">
                    Часовой пояс: {utils.localTimezoneGmtString()}
                </div>

                <Calendar
                    value={this.state.selectedDayOfMonth}
                    tileDisabled={this.calendarTileDisabled}
                    minDetail="year"
                    showNeighboringMonth={false}
                    tileClassName={({ date, view }) => {
                        if (view === "year") {
                            return "has-available-hours"
                        }

                        return utils.formatLocalDate(date) in this.state.availableLocalHours
                            ? "has-available-hours"
                            : "no-available-hours"
                    }}
                    onChange={this.onDateSelected}
                    onActiveDateChange={this.onCalendarViewChanged}
                    onDrillUp={this.onCalendarViewChanged}
                    onDrillDown={this.onCalendarViewChanged}
                />

                {this.state.selectedDayOfMonth && this.renderSelectedDayAvailableHours()}
            </>
        )
    }
}
